import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert, EyeOff, Eye, Check, Trash2, Loader2, Flag } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function FeedModerationPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["feed-reported"],
    queryFn: () => api.get("/admin/feed/reported").then((r) => r.data.posts),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["feed-reported"] });

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const hide = (p) => act(() => api.post(`/admin/feed/${p.id}/hide`), "Post hidden from the community");
  const unhide = (p) => act(() => api.post(`/admin/feed/${p.id}/unhide`), "Post restored");
  const dismiss = (p) => act(() => api.post(`/admin/feed/${p.id}/dismiss-report`), "Report dismissed");
  const del = (p) => {
    if (!window.confirm("Permanently delete this post?")) return;
    act(() => api.delete(`/feed/${p.id}`), "Post deleted");
  };

  return (
    <div data-testid="feed-moderation-panel">
      <div className="mb-5">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><ShieldAlert size={22} className="text-[#144F75]" /> Community moderation</h2>
        <p className="text-sm text-[#4A5D6B] mt-1">Reported and hidden community posts. Hide a post to remove it from the timeline, or dismiss the report if it's fine.</p>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-14"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div>
      ) : (data || []).length === 0 ? (
        <div className="eyw-card p-10 text-center text-[#4A5D6B]" data-testid="feed-mod-empty">
          <Check size={26} className="mx-auto text-[#5C8A72] mb-2" /> Nothing needs your attention — the community is clear.
        </div>
      ) : (
        <div className="space-y-3">
          {data.map((p) => (
            <div key={p.id} data-testid={`reported-post-${p.id}`} className="eyw-card p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#1A242B]">{p.user_name}</span>
                    <span className="text-xs text-[#8FA89B]">· {timeAgo(p.created_at)}</span>
                    {p.category && <span className="text-[10px] uppercase tracking-wide bg-[#144F75]/10 text-[#144F75] px-2 py-0.5 rounded-full">{p.category}</span>}
                    {p.reported && <span className="text-[10px] uppercase tracking-wide bg-[#D98E73]/15 text-[#b8694f] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Flag size={10} /> reported</span>}
                    {p.hidden && <span className="text-[10px] uppercase tracking-wide bg-[#8FA89B]/20 text-[#4A5D6B] px-2 py-0.5 rounded-full">hidden</span>}
                  </div>
                  {p.body && <p className="text-[#1A242B] text-sm mt-2 whitespace-pre-wrap">{p.body}</p>}
                  {p.image_url && <img src={p.image_url} alt="" className="rounded-xl mt-2 max-h-48 object-cover" />}
                  {(p.link_url || p.video_url) && <p className="text-xs text-[#144F75] mt-2 break-all">{p.video_url || p.link_url}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {p.hidden ? (
                  <button onClick={() => unhide(p)} data-testid={`mod-unhide-${p.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Eye size={13} /> Restore</button>
                ) : (
                  <button onClick={() => hide(p)} data-testid={`mod-hide-${p.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><EyeOff size={13} /> Hide</button>
                )}
                {p.reported && <button onClick={() => dismiss(p)} data-testid={`mod-dismiss-${p.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Check size={13} /> Dismiss report</button>}
                <button onClick={() => del(p)} data-testid={`mod-delete-${p.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !text-red-600 !border-red-300"><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
